/**
 * Estado de archivo en slots de perfil.
 * Alinea UI/validación con files.form (`habilitado` + OCR).
 */

import { UploadedTipoDocumento } from '../progress/perfil-progress.definitions';
import { formatBytesLabel } from './perfil-documentos.util';

/** NeoEstadoSolicitudDTO.ESTADO_BORRADOR */
export const PERFIL_ESTADO_SOLICITUD_BORRADOR = 1;

export interface PerfilArchivoSlotState {
  name: string;
  sizeLabel: string;
  idDocumento?: string | number | null;
  /** 1 = OK (BX/OCR); 0 = rechazado; null = sin marcar. */
  habilitado?: number | null;
  estadoDocumentoOCR?: number | null;
  observacionDocumentoOCR?: string | null;
}

export function emptyArchivoSlot(): PerfilArchivoSlotState {
  return { name: '', sizeLabel: '' };
}

export function isArchivoSlotFilled(
  slot: PerfilArchivoSlotState | null | undefined
): boolean {
  return !!slot?.name;
}

/** Rechazado por BX u OCR (mismo interruptor operativo que files.form). */
export function isArchivoSlotRechazado(
  slot: PerfilArchivoSlotState | null | undefined
): boolean {
  if (!slot?.name) {
    return false;
  }
  if (slot.habilitado != null && Number(slot.habilitado) === 0) {
    return true;
  }
  return (
    slot.estadoDocumentoOCR != null && Number(slot.estadoDocumentoOCR) === 0
  );
}

export function isArchivoSlotAprobado(
  slot: PerfilArchivoSlotState | null | undefined
): boolean {
  if (!slot?.name) {
    return false;
  }
  return slot.habilitado != null && Number(slot.habilitado) === 1;
}

/** Cuenta para progreso / required: archivo presente y no rechazado. */
export function isArchivoSlotValidoParaProgreso(
  slot: PerfilArchivoSlotState | null | undefined
): boolean {
  return isArchivoSlotFilled(slot) && !isArchivoSlotRechazado(slot);
}

/**
 * Delete deshabilitado si habilitado==1 && idEstado != borrador.
 * Docs malos o sin marcar (null) sí se pueden quitar.
 * Si el estado de solicitud aún no está en cache, no bloqueamos.
 */
export function canRemoveArchivoSlot(
  slot: PerfilArchivoSlotState | null | undefined,
  idEstadoSolicitud: number | null | undefined
): boolean {
  if (!isArchivoSlotFilled(slot)) {
    return false;
  }
  if (!isArchivoSlotAprobado(slot)) {
    return true;
  }
  if (idEstadoSolicitud == null) {
    return true;
  }
  const estado = Number(idEstadoSolicitud);
  if (Number.isFinite(estado) && estado !== PERFIL_ESTADO_SOLICITUD_BORRADOR) {
    return false;
  }
  return true;
}

/** Verde solo fuera de borrador (igual files.form). */
export function shouldShowArchivoSlotAprobadoUi(
  slot: PerfilArchivoSlotState | null | undefined,
  idEstadoSolicitud: number | null | undefined
): boolean {
  if (!isArchivoSlotAprobado(slot) || idEstadoSolicitud == null) {
    return false;
  }
  const estado = Number(idEstadoSolicitud);
  return (
    Number.isFinite(estado) && estado !== PERFIL_ESTADO_SOLICITUD_BORRADOR
  );
}

type UploadedFileDto = NonNullable<
  UploadedTipoDocumento['neoDocumentosSolicitudDTO']
>[number];

export function slotStateFromUploaded(
  uploaded: UploadedFileDto,
  fallbackName = 'Documento'
): PerfilArchivoSlotState {
  return {
    name: uploaded.nombreDocumento || fallbackName,
    sizeLabel: formatBytesLabel(uploaded.pesoArchivo),
    idDocumento: uploaded.idDocumentoSolicitud,
    habilitado:
      uploaded.habilitado != null ? Number(uploaded.habilitado) : null,
    estadoDocumentoOCR:
      uploaded.estadoDocumentoOCR != null
        ? Number(uploaded.estadoDocumentoOCR)
        : null,
    observacionDocumentoOCR: uploaded.observacionDocumentoOCR ?? null,
  };
}

export function slotStateFromUploadResult(
  file: File,
  res: {
    idDocumentoSolicitud?: string | number;
    pesoArchivo?: number | null;
    habilitado?: number | null;
    estadoDocumentoOCR?: number | null;
    observacionDocumentoOCR?: string | null;
  }
): PerfilArchivoSlotState {
  const ocr =
    res.estadoDocumentoOCR != null ? Number(res.estadoDocumentoOCR) : null;
  let habilitado =
    res.habilitado != null ? Number(res.habilitado) : null;
  if (ocr === 0 && (habilitado == null || habilitado === 1)) {
    habilitado = 0;
  }
  return {
    name: file.name,
    sizeLabel: formatBytesLabel(
      res.pesoArchivo != null ? Number(res.pesoArchivo) : file.size
    ),
    idDocumento: res.idDocumentoSolicitud,
    habilitado,
    estadoDocumentoOCR: ocr,
    observacionDocumentoOCR: res.observacionDocumentoOCR ?? null,
  };
}

/**
 * Gate de envío (validDocuments + OCR):
 * cuenta archivos de tipos obligatorios inválidos si:
 * - habilitado === 0, o
 * - estadoDocumentoOCR === 0 (aunque habilitado sea null).
 */
export function countObligatoriosConHabilitadoCero(
  items: ReadonlyArray<UploadedTipoDocumento>
): number {
  let contador = 0;
  for (const elemento of items) {
    const obligatorio =
      elemento.obligatorio === true ||
      elemento.obligatorio === 1 ||
      Number(elemento.obligatorio) === 1;
    if (!obligatorio) {
      continue;
    }
    for (const aux of elemento.neoDocumentosSolicitudDTO ?? []) {
      if (isDocumentoInvalidoParaEnvio(aux)) {
        contador++;
      }
    }
  }
  return contador;
}

/** Rechazo BX/OCR que debe bloquear el envío. */
export function isDocumentoInvalidoParaEnvio(doc: {
  habilitado?: number | null;
  estadoDocumentoOCR?: number | null;
} | null | undefined): boolean {
  if (!doc) {
    return false;
  }
  if (doc.habilitado != null && Number(doc.habilitado) === 0) {
    return true;
  }
  if (
    doc.estadoDocumentoOCR != null &&
    Number(doc.estadoDocumentoOCR) === 0
  ) {
    return true;
  }
  return false;
}
